/**
 * @file components/Series/component.js
 * @copyright 2016 CBC/Radio-Canada. All rights reserved.
 * @author Fitore Jaha [fitore.jaha@cbc.ca]
 */

import PropTypes from "prop-types";
import Card from "../Card/component";
import {
    capitalizeFirstLetter,
    normalizeClass,
} from "../../utils/path/library";
import classNames from "classnames";
import { ORIENTATION_VERTICAL, WATCH_MEDIUMS } from "./constants";
import { CARD_TYPES } from "../Card/constants";
import { Heading, SectionHeading } from "../Heading/component";
import { getScltClassName } from "../../utils/tracking/library";

/**
 * The Series component.
 *
 * This component appears on the Show sidebar and features a list of Shows etc
 *
 * @param {Object} props The component props
 * @returns {React.Component} Returns the Series component
 */
export function Series({
    baseSection = "",
    items,
    orientation = ORIENTATION_VERTICAL,
    customTitle,
}) {
    const sectionName = customTitle
        ? customTitle
        : `${capitalizeFirstLetter(baseSection)} Series`;
    const sectionClassName = normalizeClass("section", baseSection);
    const classes = classNames(
        "series",
        getScltClassName(`series${baseSection}`),
        sectionClassName,
        orientation
    );

    const isVertical = orientation === ORIENTATION_VERTICAL;

    return (
        <div className={classes} data-testid="series">
            {isVertical ? (
                <Heading
                    className="seriesHeadline"
                    text={sectionName}
                    level="h2"
                    levelClassName={"h3"}
                />
            ) : (
                <SectionHeading text={sectionName} />
            )}
            {getCards(items, baseSection, orientation)}
        </div>
    );
}

/**
 * Generate a list of Cards for each item of the series
 * @param {object} items The list of items to be displayed
 * @param {string} baseSection The vertical section, like "arts", "life", "news"
 * @param {string} orientation The direction of cards, e.g. "vertical" or "horizontal"
 * @returns {object} The JSX for the list
 */
function getCards(items, baseSection, orientation) {
    if (!items) {
        return null;
    }

    let listMarkup = [];

    // Create a Card for each item
    items
        .filter((item) => !item.hideInListing)
        .forEach((item, index) => {
            // In the horizontal orientation, cards with images are of type "show", otherwise "text"
            const cardType =
                item.image === "" ? CARD_TYPES.TYPE_TEXT : CARD_TYPES.TYPE_SHOW;
            const image = item.thumbnail ? item.thumbnail : item.image;
            const section = item.section || baseSection;
            const classes = classNames("seriesListItem", cardType, orientation);
            const description =
                typeof item.description === "undefined" ? "" : item.description;
            const flag = typeof item.medium === "undefined" ? "" : item.medium;
            const showName = item.alias ? item.alias : item.showSlug;
            const urlValue =
                showName && !item.nestedPath
                    ? `/${section}/${showName}`
                    : item.url;

            let callToActionText = item.callToActionText
                ? item.callToActionText
                : "Visit Show Site";
            if (item.medium) {
                callToActionText = WATCH_MEDIUMS.includes(item.medium)
                    ? "Watch Now"
                    : "Listen Now";
            }

            const isExternalLink = item.external === true ? true : false;
            const url = item.external ? item.url : urlValue;

            const cardProps = {
                className: `seriesItem sclt-series${baseSection}card${index}`,
                type: cardType,
                baseSection: baseSection,
                lazyLoad: false,
                category: "",
                flag: flag,
                external: isExternalLink,
                title: item.title,
                url: url,
                showSlug: item.showSlug,
                key: `seriesCard${index}`,
                imageURL: image,
                description: description,
                contentId: 1.1,
                itemType: "nonpolopolyURL",
                updatedTime: 1,
                sourceId: `${index}`,
                callToActionText: callToActionText,
            };

            listMarkup.push(
                <li className={classes} key={`seriesItem${index}`}>
                    <Card {...cardProps} />
                </li>
            );
        });

    return <ul className="seriesList">{listMarkup}</ul>;
}

Series.propTypes = {
    baseSection: PropTypes.string,
    items: PropTypes.array,
    orientation: PropTypes.string,
};

export default Series;
